'use client';

import { useMemo } from 'react';
import { alpha } from '@mui/material/styles';
import { Box, Typography } from '@/primitives';
import IconButton from '@mui/material/IconButton';
import Tooltip from '@mui/material/Tooltip';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import EbvTraitExplanationPopover from './EbvTraitExplanationPopover';
import { EbvTraitHeaderLabel } from './EbvTraitHeaderLabel';
import type { EbvTraitHeaderContext } from './ebvTraitExplanations';
import { useEbvChartLayout } from './useEbvChartLayout';
import { getGuideLabelTransform } from './guideLabel';
import {
  ebvGroupingOptionsFromDisplay,
  getEbvChartCategoryIndex,
  resolveEbvChartBarColour,
} from '@/hooks/publicSearch/ebv/ebvColumns';
import {
  ChartAxisBandAlign,
  PercentileScaleRow,
} from './EbvPercentileScaleRow';
import { EbvPercentileGraphInfoDialog } from './EbvPercentileGraphInfoDialog';
import {
  clampPercentileForChart,
  EBV_CHART_AXIS_WIDTH_PX,
  getCompactTraitLabel,
  getGraphTraitName,
  getGuidePosition,
  getLeftDirection,
} from './ebvPercentileUtils';
import type { EbvPercentileGraphProps } from './ebvPercentileTypes';

// Re-export shared types/values so existing importers keep working off one shared module.
export type {
  EbvChartItem,
  EbvPercentileGraphProps,
} from './ebvPercentileTypes';
export { ChartAxisBandAlign } from './EbvPercentileScaleRow';
export {
  EBV_CHART_AXIS_WIDTH_PX,
  getCompactTraitLabel,
} from './ebvPercentileUtils';

export default function EbvPercentileGraph({
  data,
  title,
  centerTitle = false,
  description,
  breedAverageMessage,
  traitExplanationsByKey,
  showBreedplanHelp = true,
  ebvLabel = 'EBV',
  infoOpen,
  onInfoClose,
  renderInfoDialog,
  infoButtonAriaLabel = 'About the EBV Percentile Graph',
  showInfoButton = true,
  useColourSlotGrouping = false,
}: EbvPercentileGraphProps) {
  const {
    theme,
    isDarkMode,
    useCompactTraitLabels,
    percentileScale,
    traitColumnWidth,
    leftDirectionColumnWidth,
    rightDirectionColumnWidth,
    graphInfoOpen,
    setGraphInfoOpen,
    popoverAnchor,
    openPopover,
    popoverEntry,
    handleInfoClick,
    handlePopoverClose,
  } = useEbvChartLayout();

  const effectiveInfoOpen = (infoOpen ?? false) || graphInfoOpen;
  const handleInfoClose = () => {
    setGraphInfoOpen(false);
    onInfoClose?.();
  };

  const traitHeaderContext = useMemo((): EbvTraitHeaderContext | undefined => {
    if (!traitExplanationsByKey) return undefined;
    return {
      traitExplanationsByKey,
      onTraitInfoClick: handleInfoClick,
    };
  }, [traitExplanationsByKey, handleInfoClick]);

  const ebvGroupingOptions = useMemo(
    () => ebvGroupingOptionsFromDisplay({ useColourSlotGrouping }),
    [useColourSlotGrouping],
  );

  const chartData = useMemo(() => {
    const filtered = data;

    return filtered.sort((first, second) => {
      const categoryIndexA = getEbvChartCategoryIndex(first.category);
      const categoryIndexB = getEbvChartCategoryIndex(second.category);

      if (categoryIndexA !== categoryIndexB) {
        return categoryIndexA - categoryIndexB;
      }

      // Same category, sort by categoryOrder
      const orderA = first.categoryOrder ?? Infinity;
      const orderB = second.categoryOrder ?? Infinity;
      return orderA - orderB;
    });
  }, [data]);

  if (chartData.length === 0) {
    return (
      <Box sx={{ p: 3, textAlign: 'center' }}>
        <Typography
          variant="bodysmall"
          sx={{
            color: 'text.secondary',
          }}
        >
          No {ebvLabel} percentile graph data available.
        </Typography>
      </Box>
    );
  }

  const rowHeight = 28;
  const footerPercentileCaption = breedAverageMessage?.trim() || undefined;
  const chartRowPadX = parseFloat(theme.spacing(1)) * 2;
  const chartRowMinWidthXs =
    traitColumnWidth +
    leftDirectionColumnWidth +
    EBV_CHART_AXIS_WIDTH_PX +
    rightDirectionColumnWidth +
    chartRowPadX;
  const chartRowMinWidthMd = chartRowMinWidthXs + 40;

  return (
    <Box
      sx={{
        backgroundColor: 'background.paper',
        border: 1,
        borderColor: 'divider',
        borderRadius: 3,
        boxShadow:
          '0 4px 6px -1px rgba(0, 0, 0, 0.05), 0 2px 4px -1px rgba(0, 0, 0, 0.03)',
        p: { xs: 1, md: 2.5 },
        width: '100%',
        minWidth: 0,
        maxWidth: '100%',
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
        boxSizing: 'border-box',
      }}
    >
      {title &&
        (() => {
          const titleContent = (
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: centerTitle ? 'center' : 'flex-start',
                gap: 0.5,
                minWidth: 0,
                ...(centerTitle && { textAlign: 'center' }),
              }}
            >
              <Typography
                component="span"
                sx={{
                  fontWeight: 700,
                  fontSize: 15,
                  letterSpacing: '-0.01em',
                  color: 'text.primary',
                }}
              >
                {title}
              </Typography>
              {showInfoButton && (
                <Tooltip title="About this graph" enterDelay={300} arrow>
                  <IconButton
                    size="small"
                    onClick={() => setGraphInfoOpen(true)}
                    aria-label={infoButtonAriaLabel}
                    sx={{
                      p: 0.25,
                      flexShrink: 0,
                      color: 'text.secondary',
                      '&:hover': { color: 'text.primary' },
                    }}
                  >
                    <InfoOutlinedIcon sx={{ fontSize: 18 }} />
                  </IconButton>
                </Tooltip>
              )}
            </Box>
          );

          // Long titles (e.g. the mating predictor's) overflow the narrow
          // axis band, so center them across the full card width instead.
          if (centerTitle) {
            return (
              <Box
                sx={{
                  mb: { xs: 1, md: 2 },
                  display: 'flex',
                  justifyContent: 'center',
                  width: '100%',
                }}
              >
                {titleContent}
              </Box>
            );
          }

          return (
            <ChartAxisBandAlign
              traitColumnWidth={traitColumnWidth}
              leftDirectionColumnWidth={leftDirectionColumnWidth}
              rightDirectionColumnWidth={rightDirectionColumnWidth}
              trackWidthPx={
                useCompactTraitLabels ? undefined : EBV_CHART_AXIS_WIDTH_PX
              }
              leftBeforeTrackWidthPx={useCompactTraitLabels ? 0 : undefined}
              rightAfterTrackWidthPx={useCompactTraitLabels ? 0 : undefined}
              sx={{ mb: { xs: 1, md: 2 }, display: { xs: 'none', md: 'flex' } }}
            >
              {titleContent}
            </ChartAxisBandAlign>
          );
        })()}
      {description && (
        <ChartAxisBandAlign
          traitColumnWidth={traitColumnWidth}
          leftDirectionColumnWidth={leftDirectionColumnWidth}
          rightDirectionColumnWidth={rightDirectionColumnWidth}
          trackWidthPx={
            useCompactTraitLabels ? undefined : EBV_CHART_AXIS_WIDTH_PX
          }
          leftBeforeTrackWidthPx={useCompactTraitLabels ? 0 : undefined}
          rightAfterTrackWidthPx={useCompactTraitLabels ? 0 : undefined}
          sx={{ mb: 2 }}
        >
          <Typography
            variant="bodysmall"
            sx={{
              color: 'text.secondary',
              textAlign: 'center',
              fontSize: 12,
              width: '100%',
            }}
          >
            {description}
          </Typography>
        </ChartAxisBandAlign>
      )}
      <Box
        sx={{
          width: '100%',
          minWidth: 0,
          flexGrow: 1,
          overflowX: 'auto',
        }}
      >
        <Box
          sx={{
            minWidth: { xs: 0, md: chartRowMinWidthMd },
          }}
        >
          <PercentileScaleRow
            variant="header"
            percentileScale={percentileScale}
            traitColumnWidth={traitColumnWidth}
            leftDirectionColumnWidth={leftDirectionColumnWidth}
            rightDirectionColumnWidth={rightDirectionColumnWidth}
            axisWidthPx={EBV_CHART_AXIS_WIDTH_PX}
            useFlexAxis={useCompactTraitLabels}
          />

          {/* Bars */}
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0 }}>
            {chartData.map((item) => {
              const graphTraitName = getGraphTraitName(item);
              const color = resolveEbvChartBarColour(
                item.chartColour,
                item.category,
                item.colour,
                ebvGroupingOptions,
              );
              const hasPercentile =
                item.percentile !== null && item.percentile !== undefined;
              const rawPercentile = clampPercentileForChart(
                item.percentile ?? 50,
              );
              const percentile = 100 - rawPercentile;
              const barPosition = percentile;
              const leftDirection = getLeftDirection(item.leftLabel);
              const percentileTooltip = hasPercentile
                ? `${graphTraitName}: ${rawPercentile}%`
                : '';

              return (
                <Box
                  key={item.field}
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    minHeight: rowHeight,
                    borderRadius: 1.5,
                    px: { xs: 0, md: 1 },
                    py: 0,
                    transition: 'background-color 0.15s ease',
                    '&:hover': {
                      backgroundColor: 'action.hover',
                    },
                  }}
                >
                  {/* Trait name + optional info icon (from EBV_DESCRIPTION society setting) */}
                  <Box
                    sx={{
                      width: traitColumnWidth,
                      flexShrink: 0,
                      pr: { xs: 0.25, md: 0.5 },
                      overflow: 'hidden',
                      display: 'flex',
                      alignItems: 'center',
                      gap: 0.25,
                      minWidth: 0,
                    }}
                  >
                    <Tooltip title={graphTraitName} enterDelay={300} arrow>
                      <Box component="span" sx={{ minWidth: 0, flex: 1 }}>
                        <EbvTraitHeaderLabel
                          label={
                            useCompactTraitLabels
                              ? getCompactTraitLabel({
                                  ...item,
                                  label: graphTraitName,
                                })
                              : graphTraitName
                          }
                          traitKey={item.traitKey}
                          traitHeaderContext={traitHeaderContext}
                          align="left"
                          iconLayout="inline"
                          hideIconOnMobile
                          iconSize={14}
                          typographyProps={{
                            sx: {
                              fontSize: 11,
                              fontWeight: 600,
                              whiteSpace: useCompactTraitLabels
                                ? 'nowrap'
                                : { xs: 'normal', md: 'nowrap' },
                              overflow: useCompactTraitLabels
                                ? 'hidden'
                                : { xs: 'visible', md: 'hidden' },
                              textOverflow: useCompactTraitLabels
                                ? 'ellipsis'
                                : { xs: 'clip', md: 'ellipsis' },
                            },
                          }}
                        />
                      </Box>
                    </Tooltip>
                  </Box>

                  {/* Left direction */}
                  <Box
                    sx={{
                      width: leftDirectionColumnWidth,
                      flexShrink: 0,
                      pr: { xs: 0.25, md: 1 },
                      overflow: 'hidden',
                      display: 'block',
                    }}
                  >
                    <Typography
                      variant="bodysmall"
                      sx={{
                        fontSize: 10,
                        textAlign: 'right',
                        whiteSpace: { xs: 'normal', md: 'nowrap' },
                        overflow: { xs: 'visible', md: 'hidden' },
                        textOverflow: { xs: 'clip', md: 'ellipsis' },
                        color: 'text.disabled',
                      }}
                    >
                      {leftDirection}
                    </Typography>
                  </Box>

                  {/* Bar container - centered at 50% */}
                  <Box
                    sx={{
                      width: { xs: 'auto', md: EBV_CHART_AXIS_WIDTH_PX },
                      minWidth: { xs: 0, md: EBV_CHART_AXIS_WIDTH_PX },
                      flexGrow: { xs: 1, md: 0 },
                      flexShrink: { xs: 1, md: 0 },
                      height: 24,
                      backgroundColor: 'background.paper',
                      backgroundImage: 'none',
                      border: 0,
                      borderRadius: 0,
                      position: 'relative',
                      isolation: 'isolate',
                      overflow: 'hidden',
                    }}
                  >
                    {percentileScale.map((guidePercentile) => {
                      return (
                        <Box
                          key={guidePercentile}
                          sx={{
                            position: 'absolute',
                            left: getGuidePosition(guidePercentile),
                            top: 0,
                            bottom: 0,
                            width: 0,
                            borderLeft: (theme) =>
                              `1px dashed ${alpha(theme.palette.text.secondary, 0.35)}`,
                            transform: getGuideLabelTransform(guidePercentile),
                            zIndex: 0,
                          }}
                        />
                      );
                    })}

                    {/* Bar - positioned based on percentile */}
                    {hasPercentile && percentile !== 50 && (
                      <Tooltip title={percentileTooltip} enterDelay={200} arrow>
                        <Box
                          sx={{
                            position: 'absolute',
                            top: 3,
                            bottom: 3,
                            left: percentile < 50 ? `${barPosition}%` : '50%',
                            width: `${Math.abs(percentile - 50)}%`,
                            backgroundColor: color,
                            borderRadius: 0.75,
                            minWidth: 20,
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            zIndex: 1,
                            cursor: 'default',
                          }}
                        >
                          <Typography
                            variant="bodysmall"
                            sx={{
                              display: { xs: 'block', md: 'none' },
                              fontSize: 9,
                              fontWeight: 700,
                              color: isDarkMode ? '#8a8a8a' : 'text.primary',
                              lineHeight: 1,
                              pointerEvents: 'none',
                            }}
                          >
                            {rawPercentile}%
                          </Typography>
                        </Box>
                      </Tooltip>
                    )}

                    {/* If exactly at breed average, show a marker */}
                    {hasPercentile && percentile === 50 && (
                      <Tooltip title={percentileTooltip} enterDelay={200} arrow>
                        <Box
                          sx={{
                            position: 'absolute',
                            left: 'calc(50% - 10px)',
                            top: 3,
                            bottom: 3,
                            width: 20,
                            backgroundColor: color,
                            borderRadius: 0.75,
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            zIndex: 1,
                            cursor: 'default',
                          }}
                        >
                          <Typography
                            variant="bodysmall"
                            sx={{
                              display: { xs: 'block', md: 'none' },
                              fontSize: 9,
                              fontWeight: 700,
                              color: isDarkMode ? '#8a8a8a' : 'text.primary',
                              lineHeight: 1,
                              pointerEvents: 'none',
                            }}
                          >
                            {rawPercentile}%
                          </Typography>
                        </Box>
                      </Tooltip>
                    )}
                  </Box>

                  {/* Right direction label - desktop only */}
                  <Box
                    sx={{
                      width: rightDirectionColumnWidth,
                      flexShrink: 0,
                      pl: 1,
                      overflow: 'hidden',
                      display: { xs: 'none', md: 'block' },
                    }}
                  >
                    <Typography
                      variant="bodysmall"
                      sx={{
                        fontSize: 10,
                        whiteSpace: 'nowrap',
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        color: 'text.disabled',
                      }}
                    >
                      {item.rightLabel}
                    </Typography>
                  </Box>

                  {/* Percentile (far right) - hidden on mobile, shown on bar instead */}
                  <Box
                    sx={{
                      width: 40,
                      flexShrink: 0,
                      textAlign: 'right',
                      pl: 0.5,
                      display: { xs: 'none', md: 'block' },
                    }}
                  >
                    <Typography
                      variant="bodysmall"
                      sx={{
                        fontSize: 10,
                        fontWeight: 500,
                        color: 'text.secondary',
                        fontFamily: 'monospace',
                      }}
                    >
                      {hasPercentile ? rawPercentile : ''}
                    </Typography>
                  </Box>
                </Box>
              );
            })}
          </Box>

          <PercentileScaleRow
            variant="footer"
            percentileScale={percentileScale}
            traitColumnWidth={traitColumnWidth}
            leftDirectionColumnWidth={leftDirectionColumnWidth}
            rightDirectionColumnWidth={rightDirectionColumnWidth}
            axisWidthPx={EBV_CHART_AXIS_WIDTH_PX}
            useFlexAxis={useCompactTraitLabels}
          />
        </Box>
      </Box>
      {footerPercentileCaption?.trim() && (
        <ChartAxisBandAlign
          traitColumnWidth={traitColumnWidth}
          leftDirectionColumnWidth={leftDirectionColumnWidth}
          rightDirectionColumnWidth={rightDirectionColumnWidth}
          trackWidthPx={
            useCompactTraitLabels ? undefined : EBV_CHART_AXIS_WIDTH_PX
          }
          leftBeforeTrackWidthPx={useCompactTraitLabels ? 0 : undefined}
          rightAfterTrackWidthPx={useCompactTraitLabels ? 0 : undefined}
          sx={{ pt: 1 }}
        >
          <Typography
            variant="bodysmall"
            sx={{
              textAlign: 'center',
              width: '100%',
              fontSize: { xs: 11, sm: 12 },
              fontWeight: 500,
              color: 'text.primary',
              whiteSpace: 'normal',
              lineHeight: 1.35,
            }}
          >
            {footerPercentileCaption.trim()}
          </Typography>
        </ChartAxisBandAlign>
      )}
      {renderInfoDialog ? (
        renderInfoDialog(effectiveInfoOpen, handleInfoClose)
      ) : (
        <EbvPercentileGraphInfoDialog
          open={effectiveInfoOpen}
          onClose={handleInfoClose}
        />
      )}
      <EbvTraitExplanationPopover
        open={openPopover}
        anchorEl={popoverAnchor}
        onClose={handlePopoverClose}
        entry={popoverEntry}
        showBreedplanHelp={showBreedplanHelp}
      />
    </Box>
  );
}
